/**
 * Settings Page Component
 * Allows users to configure their preferences
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Switch,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { usePreferences } from '../../contexts/PreferencesContext';

function SettingsPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    preferences,
    loading,
    error,
    updatePreferences,
    resetPreferences,
    DEFAULT_PREFERENCES
  } = usePreferences();

  // Local state for form
  const [formValues, setFormValues] = useState(preferences);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Update local form when preferences load
  React.useEffect(() => {
    setFormValues(preferences);
  }, [preferences]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormValues(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await updatePreferences(formValues);
    setSaving(false);

    if (result.success) {
      setSnackbar({ open: true, message: 'Settings saved successfully', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: result.error || 'Failed to save settings', severity: 'error' });
    }
  };

  const handleReset = async () => {
    setSaving(true);
    const result = await resetPreferences();
    setSaving(false);

    if (result.success) {
      setFormValues(DEFAULT_PREFERENCES);
      setSnackbar({ open: true, message: 'Settings reset to defaults', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: result.error || 'Failed to reset settings', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Check for unsaved changes
  const hasChanges = JSON.stringify(formValues) !== JSON.stringify(preferences);

  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <SettingsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Sign in to access settings
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You need to be signed in to customize your preferences.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login', { state: { from: '/settings' } })}
          >
            Sign In
          </Button>
        </Paper>
      </Container>
    );
  }

  if (loading && !formValues) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading settings...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Customize your search experience
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search Preferences
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Results per page */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <FormLabel id="results-per-page-label">Results per page</FormLabel>
          <Select
            labelId="results-per-page-label"
            value={formValues.results_per_page}
            onChange={handleChange('results_per_page')}
            size="small"
            sx={{ maxWidth: 200, mt: 1 }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Number of results to display on each search page
          </Typography>
        </FormControl>

        {/* Default sort order */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <FormLabel id="default-sort-label">Default sort order</FormLabel>
          <Select
            labelId="default-sort-label"
            value={formValues.default_sort}
            onChange={handleChange('default_sort')}
            size="small"
            sx={{ maxWidth: 200, mt: 1 }}
          >
            <MenuItem value="relevance">Relevance</MenuItem>
            <MenuItem value="date_desc">Newest first</MenuItem>
            <MenuItem value="date_asc">Oldest first</MenuItem>
            <MenuItem value="name_asc">Name (A-Z)</MenuItem>
            <MenuItem value="name_desc">Name (Z-A)</MenuItem>
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            How to order search results by default
          </Typography>
        </FormControl>

        {/* Snippet length */}
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Preview snippet length</FormLabel>
          <RadioGroup
            row
            value={formValues.snippet_length}
            onChange={handleChange('snippet_length')}
            sx={{ mt: 1 }}
          >
            <FormControlLabel value="short" control={<Radio />} label="Short" />
            <FormControlLabel value="medium" control={<Radio />} label="Medium" />
            <FormControlLabel value="long" control={<Radio />} label="Long" />
          </RadioGroup>
          <Typography variant="caption" color="text.secondary">
            Length of text previews in search results
          </Typography>
        </FormControl>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Display Preferences
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Theme */}
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Theme</FormLabel>
          <RadioGroup
            row
            value={formValues.theme}
            onChange={handleChange('theme')}
            sx={{ mt: 1 }}
          >
            <FormControlLabel value="light" control={<Radio />} label="Light" />
            <FormControlLabel value="dark" control={<Radio />} label="Dark" />
            <FormControlLabel value="system" control={<Radio />} label="System" />
          </RadioGroup>
          <Typography variant="caption" color="text.secondary">
            Choose your preferred color theme. "System" follows your device setting.
          </Typography>
        </FormControl>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Notification Preferences
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Email notifications */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="body1">Email notifications</Typography>
            <Typography variant="caption" color="text.secondary">
              Receive email alerts for saved searches (coming soon)
            </Typography>
          </Box>
          <Switch
            checked={formValues.email_notifications}
            onChange={handleChange('email_notifications')}
          />
        </Box>

        {/* Email frequency */}
        {formValues.email_notifications && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <FormLabel id="email-frequency-label">Email frequency</FormLabel>
            <Select
              labelId="email-frequency-label"
              value={formValues.email_frequency}
              onChange={handleChange('email_frequency')}
              size="small"
              sx={{ maxWidth: 200, mt: 1 }}
            >
              <MenuItem value="immediate">Immediate</MenuItem>
              <MenuItem value="daily">Daily digest</MenuItem>
              <MenuItem value="weekly">Weekly digest</MenuItem>
            </Select>
          </FormControl>
        )}
      </Paper>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleReset}
          disabled={saving}
        >
          Reset to Defaults
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default SettingsPage;
